import React, { useState } from "react";
import { useTurnstile, AVISO_TURNSTILE } from "@/components/Turnstile";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { API, formatApiError } from "@/lib/api";
import { InstallDialog, isStandalone } from "@/components/InstallApp";
import { Eye, EyeOff, Loader2, LogIn, QrCode } from "lucide-react";
import EntrarComCelular from "@/components/EntrarComCelular";
import { useSite, loginTextos } from "@/components/useSite";
import { RotatingTestimonial } from "@/components/Testimonials";

export default function Login() {
  const { login, setUser } = useAuth();
  const navigate = useNavigate();
  // "Lembrar meu acesso": só o e-mail/CPF fica guardado neste aparelho; a senha quem guarda é o gerenciador de senhas do navegador
  const REMEMBER_KEY = "iep-remember-email";
  const [email, setEmail] = useState(() => { try { return localStorage.getItem(REMEMBER_KEY) || ""; } catch { return ""; } });
  const [remember, setRemember] = useState(() => { try { return !!localStorage.getItem(REMEMBER_KEY); } catch { return false; } });
  const [password, setPassword] = useState("");
  const [verSenha, setVerSenha] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const robo = useTurnstile("login");
  const [installOpen, setInstallOpen] = useState(false);
  const site = useSite();
  const [modo, setModo] = useState("senha");   // senha | qr (entrar com o celular)
  const [notice] = useState(() => {
    try { const m = sessionStorage.getItem("iep-session-msg"); sessionStorage.removeItem("iep-session-msg"); return m || ""; }
    catch (e) { return ""; }
  });

  const seguir = (u) => {
    const qs = new URLSearchParams(window.location.search);
    // veio do sistema do salão ("Entrar com minha conta IEP"): volta para lá já logada
    if (!u.onboarding_required && qs.get("next") === "salao") {
      window.location.href = `${API}/sso/salao`;
      return;
    }
    if (!u.onboarding_required && qs.get("next") === "keyla") {
      navigate("/keyla", { replace: true });
      return;
    }
    if (!u.onboarding_required && qs.get("next") === "tricolab") {
      navigate("/tricolab", { replace: true });
      return;
    }
    // leu o QR do computador sem estar logada no celular: depois de entrar, volta para liberar o computador
    if (!u.onboarding_required && qs.get("next") === "computador") {
      navigate(`/entrar-computador${qs.get("p") ? `?p=${encodeURIComponent(qs.get("p"))}` : ""}`, { replace: true });
      return;
    }
    navigate(u.onboarding_required ? "/primeiro-acesso" : u.role === "admin" ? "/admin" : u.role === "instructor" ? "/prof" : "/app", { replace: true });
  };

  const entrouPeloCelular = (u) => { setUser(u); seguir(u); };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (robo.falta) { setError(AVISO_TURNSTILE); return; }
    setLoading(true);
    try {
      const u = await login(email, password, robo.usar());
      try { if (remember) localStorage.setItem(REMEMBER_KEY, email.trim()); else localStorage.removeItem(REMEMBER_KEY); } catch { /* navegador sem armazenamento */ }
      seguir(u);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  // textos editáveis na tela Site → Tela de entrada (vazio = texto padrão)
  const tx = loginTextos(site);
  // "IEP INTELLIGENCE" aparece em verde onde estiver escrito
  const destaca = (t) => <span>{t.split(/(IEP INTELLIGENCE)/i).map((p, i) => (/^IEP INTELLIGENCE$/i.test(p) ? <b key={i} className="text-[#1E9E2A]">{p}</b> : p))}</span>;
  const itens = tx.itens.map(destaca);
  const ICONES_FAIXA = [
    <path key="a" d="M4 11h16a8 8 0 0 1-16 0zM9 4l6 7" />,
    <path key="a" d="M9 3h6M10 3v6l-5 9a2 2 0 0 0 2 3h10a2 2 0 0 0 2-3l-5-9V3" />,
    <><path d="M2 9l10-5 10 5-10 5z" /><path d="M6 11v5c3 2 9 2 12 0v-5" /></>,
    <path key="a" d="M4 20V10M10 20V4M16 20v-8M22 20H2" />,
    <><circle cx="12" cy="5" r="2.5" /><circle cx="5" cy="18" r="2.5" /><circle cx="19" cy="18" r="2.5" /><path d="M12 7.5v4M12 11.5 6.5 16M12 11.5l5.5 4.5" /></>,
  ];
  const faixa = tx.faixa.map((t, i) => { const [a, b = ""] = String(t).split("|"); return [a, b, ICONES_FAIXA[i]]; });
  const Icone = ({ children, className = "h-5 w-5" }) => <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">{children}</svg>;

  return (
    <div className="login-palco" data-testid="login-cover">
      <img className="login-frascos" src="/brand/login-arte-frascos.webp" alt="" />
      <img className="login-mulher" src="/brand/login-arte-mulher.webp" alt="" />

      <div className="login-texto">
        <img src="/brand/logo.png" alt="IEP – In'Nova Educação Profissional" className="h-[52px] lg:h-[110px] w-auto mb-2 lg:mb-3" />
        <div className="hidden lg:block text-[13px] tracking-[5px] font-bold text-[#232D4F]">{tx.kicker}</div>
        <h1 className="font-display font-extrabold text-[#1A2340] text-[22px] leading-tight mt-1.5 lg:text-[44px] lg:leading-[1.08] lg:mt-4">
          {tx.titulo}
        </h1>
        <p className="hidden lg:block text-xl text-slate-700 mt-2">{tx.subtitulo}</p>
        <ul className="hidden lg:grid gap-2.5 mt-6">
          {itens.map((t, k) => (
            <li key={k} className="flex items-center gap-3 text-[17px] font-semibold text-slate-800">
              <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full border-2 border-[#1E9E2A] text-[#1E9E2A]"><Icone className="h-3.5 w-3.5"><path d="m5 12 5 5 9-10" /></Icone></span>{t}
            </li>
          ))}
        </ul>
        <RotatingTestimonial items={site?.testimonials} className="hidden lg:block mt-[26px] max-w-[560px]" />
      </div>

      <div className="login-cartao" data-testid="login-form-panel">
        <div className="login-selo" aria-hidden="true"><Icone className="mx-auto mb-1 h-5 w-5"><path d="M12 3 5 6v5c0 4.5 3 8 7 10 4-2 7-5.5 7-10V6z" /><path d="m9 12 2 2 4-4" /></Icone>Ambiente seguro</div>
        {modo === "qr" ? <EntrarComCelular aoEntrar={entrouPeloCelular} voltar={() => setModo("senha")} /> : (<>
        <div className="text-center mb-6">
          <img src="/brand/logo.png" alt="" className="hidden lg:block mx-auto h-[60px] w-auto" />
          <h2 className="font-display text-2xl font-bold text-[#1A2340] lg:mt-2.5">{tx.cartao_titulo}</h2>
          <p className="text-sm text-slate-500 mt-1">{tx.cartao_sub}</p>
        </div>

        {notice && (
          <div className="mb-5 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2.5" data-testid="session-ended-notice">{notice}</div>
        )}
        <form onSubmit={submit} className="space-y-3.5">
          <div className="login-campo">
            <Icone><rect x="3" y="5" width="18" height="14" rx="2" /><path d="m3 7 9 6 9-6" /></Icone>
            <input id="email" name="username" autoComplete="username" type="text" inputMode="email" autoCapitalize="none" spellCheck={false}
              aria-label="E-mail ou CPF" placeholder="E-mail ou CPF" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="login-email-input" />
          </div>
          <div className="login-campo">
            <Icone><rect x="5" y="11" width="14" height="10" rx="2" /><path d="M8 11V8a4 4 0 0 1 8 0v3" /></Icone>
            <input id="password" name="password" autoComplete="current-password" type={verSenha ? "text" : "password"} aria-label="Senha"
              placeholder="Senha ou código de 1º acesso" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="login-password-input" />
            <button type="button" onClick={() => setVerSenha((v) => !v)} className="text-slate-500 hover:text-slate-800" aria-label={verSenha ? "Esconder senha" : "Mostrar senha"}>
              {verSenha ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
            </button>
          </div>
          <div className="text-right -mt-1">
            <Link to="/esqueci-senha" className="text-[13.5px] font-bold text-[#1E9E2A] hover:underline" data-testid="forgot-password-link">Esqueci minha senha</Link>
          </div>
          <label className="flex items-center gap-2.5 text-sm text-slate-600 cursor-pointer select-none" data-testid="login-remember-label">
            <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} className="h-[18px] w-[18px] rounded border-slate-300 accent-[#1E9E2A]" data-testid="login-remember" />
            Lembrar meu acesso neste aparelho
          </label>

          {error && (
            <div data-testid="login-error" className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">{error}</div>
          )}

          {robo.caixa}
          <button type="submit" disabled={loading} data-testid="login-submit-button"
            className="flex h-[54px] w-full items-center justify-center gap-2.5 rounded-[13px] text-[16.5px] font-bold text-white disabled:opacity-70"
            style={{ background: "linear-gradient(180deg,#24b032,#1a8a24)", boxShadow: "0 10px 22px rgba(30,158,42,.33)" }}>
            {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <><LogIn className="h-5 w-5" /> Entrar na plataforma</>}
          </button>
        </form>
        <div className="hidden md:flex items-center gap-3 text-[13px] text-slate-400 my-4 before:h-px before:flex-1 before:bg-slate-200 after:h-px after:flex-1 after:bg-slate-200">ou</div>
        <button type="button" onClick={() => setModo("qr")} className="hidden md:flex h-[54px] w-full items-center justify-center gap-2.5 rounded-[13px] border-[1.5px] border-[#dbe3ee] bg-white text-[16px] font-bold text-[#232D4F] hover:bg-slate-50" data-testid="login-qr-button">
          <QrCode className="h-5 w-5" /> Entrar com o celular (QR code)
        </button>

        <p className="text-center text-[13.5px] text-slate-600 mt-4" data-testid="first-access-hint">
          Primeira vez na plataforma nova? <Link to="/esqueci-senha" className="font-bold text-[#1E9E2A] hover:underline">Crie sua senha</Link>
        </p>

        <div className="grid grid-cols-3 mt-5 pt-4 border-t border-slate-100 text-center text-xs leading-snug text-slate-600">
          {[["Seus dados", "protegidos", <><path d="M12 3 5 6v5c0 4.5 3 8 7 10 4-2 7-5.5 7-10V6z" /><path d="m9 12 2 2 4-4" /></>],
            ["Acesso", "24 horas", <><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></>],
            ["Computador,", "celular e tablet", <><rect x="2" y="4" width="15" height="11" rx="1.5" /><path d="M6 19h7" /><rect x="17" y="9" width="5" height="11" rx="1" /></>]].map(([a, b, ic], k) => (
            <div key={k} className={k ? "border-l border-slate-100" : ""}><span className="block text-[#232D4F] mb-1.5"><Icone className="mx-auto h-6 w-6">{ic}</Icone></span>{a}<br />{b}</div>
          ))}
        </div>

        <p className="text-center text-xs text-slate-400 mt-5">
          <button onClick={() => navigate("/cursos")} className="font-bold text-[#232D4F] hover:underline" data-testid="see-courses-link">Ver cursos →</button>
          {" · "}<Link to="/termos" className="hover:text-slate-600 hover:underline">Termos</Link>
          {" · "}<Link to="/privacidade" className="hover:text-slate-600 hover:underline">Privacidade</Link>
          {!isStandalone() && <>{" · "}<button onClick={() => setInstallOpen(true)} className="text-[#1E9E2A] font-semibold hover:underline" data-testid="login-install">📱 Instalar app</button></>}
        </p>
        </>)}
        <InstallDialog open={installOpen} onOpenChange={setInstallOpen} />
      </div>
      <RotatingTestimonial items={site?.testimonials} className="lg:hidden relative z-[3] mx-3.5 mt-4" />

      <div className="login-faixa" aria-hidden="true">
        {faixa.map(([a, b, ic], k) => (
          <div key={k} className="relative w-28 text-center text-[13.5px] font-semibold leading-tight text-white">
            <span className="mx-auto mb-2 grid h-[58px] w-[58px] place-items-center rounded-full border-2 border-white/75 shadow-lg" style={{ background: "linear-gradient(145deg,#1E9E2A,#0f6b19)" }}><Icone className="h-7 w-7">{ic}</Icone></span>
            {a}<br />{b}
          </div>
        ))}
      </div>
      <div className="login-rodape">{new Date().getFullYear()} © IEP – In&apos;Nova Educação Profissional · Desenvolvido por <strong>In&apos;Nova Art and Sciences</strong></div>
    </div>
  );
}
